import React, { Fragment } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

import colors from "core/colors";

const createHelpers = (width, height, css) => {
    // somehow sizes is ending up in markup, even if it is not a valid svg attribute
    // until we have a better solution, just render it empty, instead to '[Object object]'
    const sanitizeSizes = sizes =>
        Object.defineProperty(sizes, "toString", {
            value: () => "",
            enumerable: false
        });

    const getDimensions = (size, sizes) => {
        if (
            size &&
            typeof size.width === "number" &&
            typeof size.height === "number"
        ) {
            return size;
        }

        return size && sizes[size] ? sizes[size] : { width, height };
    };

    const getCss = (size, sizes, fillColor, fillColorRule, noStyles) => {
        if (noStyles) {
            return "";
        }

        const dimensions = getDimensions(size, sizes);
        const fillRule =
            fillColor && fillColorRule
                ? `${fillColorRule}{ fill: ${fillColor}; }`
                : "";

        return css`
            width: ${dimensions.width}px;
            height: ${dimensions.height}px;
            ${fillRule}
        `;
    };

    const propsToCss = ({ size, sizes, fillColor, fillColorRule, noStyles }) =>
        getCss(size, sizes, fillColor, fillColorRule, noStyles);

    return {
        getCss,
        getDimensions,
        propsToCss,
        sanitizeSizes
    };
};

const width = "41";
const height = "40";
const viewBox = "0 0 41 40";

const { getDimensions, getCss, propsToCss, sanitizeSizes } = createHelpers(
    width,
    height,
    css
);

const sizes = sanitizeSizes({
    small: { width: 17, height: 17 },
    medium: { width: 24, height: 24 }
});

const Image = styled.svg`
    ${propsToCss}
`;

const children = (
    <Fragment>
        <path
            fill={colors.secondary.oolong}
            fillRule="evenodd"
            d="M40.663 1.566a2.01 2.01 0 01-.134-.037h-.025a20.526 20.526 0 01-.587-.147h-.024l-.05-.012-.097-.024h-.025l-.073-.013a50.087 50.087 0 00-5.076-.917 49.552 49.552 0 00-5.395-.404C28.823 0 28.456 0 28.101 0c-1.126 0-2.275.037-3.389.11h-.073l.012.073A5.084 5.084 0 0123.38 4.22a5.139 5.139 0 01-3.376 1.664c-.135.012-.27.012-.392.012a5.117 5.117 0 01-3.45-1.333 5.128 5.128 0 01-1.54-2.68l-.013-.073-.073.025c-1.7.477-3.388 1.04-5.028 1.7a50.848 50.848 0 00-6.948 3.4c-.391.233-.782.466-1.162.71-.012 0-.036.025-.06.037a5.02 5.02 0 01-.307.183c-.208.135-.244.147-.208.208l4.343 9.59.037.074.06-.037a50.051 50.051 0 014.845-2.47c.11-.05.208-.098.318-.135L13.85 29.32l.024.086.086-.05c.905-.488 1.664-.855 2.398-1.161.905-.367 1.749-.66 2.568-.893a17.035 17.035 0 014.637-.649c.244 0 .489 0 .734.013.746.036 1.712.11 2.605.464.416.16.734.355.966.6.27.269.404.636.404 1.04-.012.82-.648 1.504-1.186 1.932-.343.282-.734.49-1.028.66-.367.197-.759.392-1.174.551-.783.318-1.615.563-2.557.759-.93.195-1.835.306-2.703.342H18.963a6.19 6.19 0 01-.673-.036 3.121 3.121 0 01-.636-.11h-.024c-.208-.05-.281-.086-.294-.11.098-.147.257-.258.392-.343.159-.098.342-.208.538-.306a9.13 9.13 0 011.223-.477c.808-.257 1.7-.428 2.63-.514.44-.049.893-.06 1.346-.073h.159c.146 0 .33 0 .501.012a.34.34 0 01.11.012c.062 0 .123.013.184.013h.024c.477-.16 2.092-.771 2.545-1.823l.024-.061-.061-.025c-.025-.012-.049-.024-.073-.024-.013 0-.025-.012-.037-.012-.171-.05-.404-.123-.636-.184a10.61 10.61 0 00-1.346-.232 12.423 12.423 0 00-1.504-.086c-.416 0-.832.025-1.248.061-1.003.098-1.884.233-2.679.44-.966.245-1.774.551-2.471.918-.88.465-1.492 1.015-1.835 1.651-.38.698-.391 1.53-.049 2.178.343.636 1.003 1.088 1.97 1.333.636.171 1.357.245 2.19.245.146 0 .305 0 .464-.013a17.432 17.432 0 002.691-.305 17.696 17.696 0 002.606-.697c.917-.319 1.7-.673 2.41-1.077.11-.061.195-.11.269-.159l.195-.134v.085c0 .098-.012.184-.024.294-.061.367-.22.721-.465 1.052-.404.538-1.003 1.015-1.86 1.443a13 13 0 01-2.433.917c-.857.233-1.737.429-2.63.563l-.563.074-.11.012c-.05.012-.11.012-.16.024-.036 0-.122.013-.122.013l-.06.012v.06l.134 2.3V40l.073-.012.367-.037c.428-.049.893-.11 1.358-.196 2.006-.342 3.67-.856 5.076-1.553 1.823-.905 3.022-2.067 3.535-3.462a4.76 4.76 0 00.208-2.667 3.559 3.559 0 00-.195-.611c-.012-.037-.037-.074-.05-.11-.024-.05-.06-.11-.073-.16-.012-.036.037-.11.074-.146.012-.025.037-.037.049-.061.232-.367.403-.77.501-1.187.208-.832.172-1.712-.134-2.495-.282-.746-.832-1.407-1.59-1.884-.673-.428-1.48-.746-2.471-.954a13.145 13.145 0 00-2.692-.257c-1.871 0-3.694.245-5.419.734-.893.244-1.822.575-2.935 1.015L11.93 12.11l-.025-.073-.073.024A48.86 48.86 0 006.84 14.24c-.233.122-.465.232-.697.354l-2.508-5.7c.037-.025.122-.073.22-.135.025-.012.037-.012.037-.024.746-.44 1.517-.856 2.275-1.248a52.496 52.496 0 014.734-2.091c.783-.294 1.59-.588 2.385-.845A7.377 7.377 0 0018.131 8a7.344 7.344 0 004.024-.318 7.389 7.389 0 003.083-2.202c.404-.49.747-1.04 1.016-1.615.134-.293.256-.599.342-.905.05-.159.086-.318.122-.477.013-.073.037-.159.05-.232h.22c.367-.012.733-.012 1.1-.012 1.456 0 2.924.06 4.367.195 1.799.16 3.609.428 5.383.795h.024c.16.037.245.05.306.062l-1.26 6.177c-.147-.012-.514-.073-.77-.11-.196-.025-.343-.05-.38-.05-.452-.06-.917-.11-1.37-.146a69.943 69.943 0 00-2.764-.196c-.453-.024-.93-.036-1.383-.036h-.183v.073l-.575 13.64c0 .036.012.073.073.097h.013c.195.098.391.196.562.294.404.232.747.489 1.04.758.073.073.147.147.208.22a.794.794 0 01.098.11.268.268 0 01.037.061l.122.22.012-.244.612-12.917c1.786.097 3.547.293 5.37.611.22.037.452.074.685.123l.085.012c.22.037.22.037.233.037h.061l.012-.062 2.092-10.3c.012-.06-.049-.073-.135-.097z"
            clipRule="evenodd"
            key="key-0"
        />
    </Fragment>
);

const defaultProps = {
    children,
    viewBox,
    fillColor: null,
    fillColorRule: "&&& path, &&& use, &&& g",
    sizes,
    size: null
};

Image.propTypes /* remove-proptypes */ = {
    fillColor: PropTypes.string,
    fillColorRule: PropTypes.string,
    viewBox: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    size: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            height: PropTypes.number.isRequired,
            width: PropTypes.number.isRequired
        })
    ]),
    sizes: PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number
    })
};

export default Object.assign(Image, {
    getDimensions,
    getCss,
    defaultProps,
    displayName: "ApiCustomerTeespringLogo"
});
